<script setup>
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../../../SettingsLayout.vue';
import MessageTemplateModal from './component/MessageTemplateModal.vue';
import MessageTemplateTableBody from './component/MessageTemplateTableBody.vue';
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const showMessageTemplateModal = ref(false);
const messageTemplateModalMode = ref('add');
const selectedWhatsappTemplate = ref({});
const showDeleteConfirmationPopup = ref(false);
const searchValue = ref('');
const disabledSyncButton = ref(false);
const agentBot = ref('');

const deleteConfirmText = computed(
  () =>
    `${t('CUSTOM_ROLE.DELETE.CONFIRM.YES')} ${selectedWhatsappTemplate.value?.name}`
);

const deleteRejectText = computed(
  () =>
    `${t('CUSTOM_ROLE.DELETE.CONFIRM.NO')} ${selectedWhatsappTemplate.value?.name}`
);

const deleteMessage = computed(() => {
  return ` ${selectedWhatsappTemplate.value?.name} ? `;
});

const tableHeaders = computed(() => {
  return [
    t('WHATSAPP_TEMPLATES_MGMT.LIST.TABLE_HEADER.NAME'),
    t('WHATSAPP_TEMPLATES_MGMT.LIST.TABLE_HEADER.TYPE'),
    t('WHATSAPP_TEMPLATES_MGMT.LIST.TABLE_HEADER.LANGUAGE'),
    t('WHATSAPP_TEMPLATES_MGMT.LIST.TABLE_HEADER.STATUS'),
    t('WHATSAPP_TEMPLATES_MGMT.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const openAddModal = () => {
  messageTemplateModalMode.value = 'add';
  selectedWhatsappTemplate.value = {};
  showMessageTemplateModal.value = true;
};

const openEditModal = template => {
  messageTemplateModalMode.value = 'edit';
  selectedWhatsappTemplate.value = template;
  showMessageTemplateModal.value = true;
};

const hideMessageTemplateModal = () => {
  selectedWhatsappTemplate.value = {};
  showMessageTemplateModal.value = false;
};

const openDeletePopup = template => {
  showDeleteConfirmationPopup.value = true;
  selectedWhatsappTemplate.value = template;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteTemplate = async () => {
  try {
    await store.dispatch('inboxes/deleteTemplate', {
      inboxId: props.inbox.id,
      payload: {
        hsm_id: selectedWhatsappTemplate.value.id,
        name: selectedWhatsappTemplate.value.name,
      },
    });
    useAlert(t('WHATSAPP_TEMPLATES_MGMT.FORM.API.DELETE_SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('WHATSAPP_TEMPLATES_MGMT.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteTemplate();
  selectedWhatsappTemplate.value = {};
};

const filteredRecords = () =>
  Array.isArray(props.inbox.message_templates)
    ? props.inbox.message_templates.filter(template =>
      template.name.toLowerCase().includes(searchValue.value.toLowerCase())
    )
    : [];

const syncTemplates = async () => {
  disabledSyncButton.value = true;

  setTimeout(() => {
    disabledSyncButton.value = false;
  }, 4000);

  try {
    await store.dispatch('inboxes/syncTemplate', {
      inboxId: props.inbox.id,
    });
    useAlert(t('WHATSAPP_TEMPLATES_MGMT.FORM.API.SYNC_SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('WHATSAPP_TEMPLATES_MGMT.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const fetchAgentBots = async () => {
  await store.dispatch('agentBots/get');
  agentBot.value = store.getters['agentBots/getBotByName'](
    props.inbox.phone_number
  );
};

onMounted(() => {
  fetchAgentBots();
});
</script>

<template>
  <SettingsLayout
    class="px-8 !gap-0"
    :no-records-found="!filteredRecords().length"
    :no-records-message="t('WHATSAPP_TEMPLATES_MGMT.LIST.404')"
  >
    <template #header>
      <div class="flex items-center justify-between flex-shrink-0 gap-4 pt-4">
        <div class="flex items-center gap-2">
          <Input
            v-model="searchValue"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            :placeholder="t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            type="search"
          >
            <template #prefix>
              <Icon
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                icon="i-lucide-search"
              />
            </template>
          </Input>
        </div>
        <div class="flex gap-2">
          <woot-button
            :is-disabled="disabledSyncButton"
            class="clear"
            color-scheme="info"
            icon="download"
            @click="syncTemplates"
          >
            {{ t('WHATSAPP_TEMPLATES_MGMT.HEADER.SYNC_TEMPLATES') }}
          </woot-button>
          <woot-button
            class="clear"
            color-scheme="success"
            icon="add-circle"
            @click="openAddModal"
          >
            {{ t('WHATSAPP_TEMPLATES_MGMT.HEADER.NEW_TEMPLATE') }}
          </woot-button>
        </div>
      </div>
    </template>

    <template #body>
      <table
        class="min-w-full overflow-x-auto divide-y divide-slate-75 dark:divide-slate-700"
      >
        <thead>
          <th
            v-for="(thHeader, index) in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left text-slate-700 dark:text-slate-300"
            :class="[
              index === tableHeaders.length - 1 ? 'text-right' : '',
              index === tableHeaders.length - 1 ? '' : 'pr-4',
            ]"
          >
            <span class="mb-0">
              {{ thHeader }}
            </span>
          </th>
        </thead>

        <MessageTemplateTableBody
          :templates="filteredRecords()"
          @delete="openDeletePopup"
          @edit="openEditModal"
        />
      </table>
    </template>

    <woot-modal
      v-model:show="showMessageTemplateModal"
      :on-close="hideMessageTemplateModal"
    >
      <MessageTemplateModal
        :mode="messageTemplateModalMode"
        :selected-template="selectedWhatsappTemplate"
        :inbox-id="inbox.id"
        :agent-bot="agentBot"
        @close="hideMessageTemplateModal"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :confirm-text="deleteConfirmText"
      :message="t('WHATSAPP_TEMPLATES_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :reject-text="deleteRejectText"
      :title="t('WHATSAPP_TEMPLATES_MGMT.DELETE.CONFIRM.TITLE')"
    />
  </SettingsLayout>
</template>
